import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router'

import { type Taxonomia, useGruposDeUnNuevo, useTaxonomia } from '../api/estructura'
import { Aviso, Esqueleto } from '../componentes/ui'
import { Editor, type PestanaExtra } from '../motor/Editor'
import { useEsquema } from '../motor/esquema'
import { Listado } from '../motor/Listado'
import type { Contexto } from '../paginas/Modulo'
import { PestanaDeGrupo } from '../paginas/PestanaDeGrupo'

/**
 * Los términos de una taxonomía (plan del CMS dinámico §2.3): «Miraflores», «San Isidro»… de
 * «Distritos». Se listan y se editan desde su taxonomía —una pestaña de su editor y su propia
 * pantalla, `…/taxonomias/<id>/terminos`—, porque un término no tiene sentido fuera de ella. Un
 * término nuevo nace en esa taxonomía, y no cambia de taxonomía.
 */
const basesDe = (espacio: number, sitio: number, taxonomia: number) => `/e/${espacio}/s/${sitio}/taxonomias/${taxonomia}/terminos`

/** La lista de términos de una taxonomía: en su pantalla, o dentro del editor de la taxonomía (`compacto`). */
export const ListaDeTerminos = ({ taxonomia, espacio, sitio, compacto = false, puedeCrear }: { compacto?: boolean; espacio: number; puedeCrear: boolean; sitio: number; taxonomia: Taxonomia }) => {
  const { data: esquema } = useEsquema('terminos')
  // Dentro de otra pantalla, la búsqueda y la página no van en la dirección (moverían esa pantalla).
  const [parametros, setParametros] = useState(() => new URLSearchParams())
  return (
    <Listado
      base={basesDe(espacio, sitio, taxonomia.id)}
      columnas={esquema?.columnas.filter((c) => c !== 'taxonomia')}
      coleccion="terminos"
      compacto={compacto}
      descripcion={compacto ? undefined : `Los términos con los que se clasifica en «${taxonomia.nombrePlural}». Uno puede colgar de otro si la taxonomía es jerárquica.`}
      espacio={espacio}
      etiquetas={{ singular: taxonomia.nombre, plural: taxonomia.nombrePlural }}
      parametros={compacto ? { valor: parametros, poner: setParametros } : undefined}
      puedeCrear={puedeCrear}
      sitio={sitio}
      vacio={`Crea el primero: los ${taxonomia.nombrePlural.toLowerCase()} son con lo que se clasifican y se filtran tus contenidos.`}
      where={{ 'where[taxonomia][equals]': String(taxonomia.id) }}
    />
  )
}

export const PaginaDeTerminos = () => {
  const { taxonomia: enLaRuta, id } = useParams()
  const { espacio, sitio, permisos } = useOutletContext<Contexto>()
  const idDeLaTaxonomia = enLaRuta && /^\d+$/.test(enLaRuta) ? Number(enLaRuta) : null
  const { data: taxonomia, isPending, isError } = useTaxonomia(idDeLaTaxonomia)
  const idNumerico = id !== undefined && /^\d+$/.test(id) ? Number(id) : null
  // Los grupos de campos de los términos (la regla «la taxonomía es…»): con el esquema y su id, o con la taxonomía para uno nuevo.
  const { data: esquemaConGrupos } = useEsquema('terminos', { id: idNumerico, enabled: idNumerico !== null })
  const { data: gruposDelNuevo } = useGruposDeUnNuevo('terminos', sitio?.id ?? null, { taxonomia: idDeLaTaxonomia }, id === 'nuevo' && idDeLaTaxonomia !== null)

  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (idDeLaTaxonomia === null || isError) return <Aviso tono="error">Esa taxonomía no existe, o no es de esta web.</Aviso>
  if (isPending || !taxonomia) return <Esqueleto filas={8} />
  const puedeEditar = permisos.tiene('terminos.editar')

  if (id === undefined) return <ListaDeTerminos espacio={espacio} puedeCrear={puedeEditar} sitio={sitio.id} taxonomia={taxonomia} />
  if (id !== 'nuevo' && idNumerico === null) return <Aviso tono="error">Ese término no existe, o no es tuyo.</Aviso>

  const grupos = idNumerico !== null ? (esquemaConGrupos?.grupos ?? []) : (gruposDelNuevo?.grupos ?? [])
  const pestanas: PestanaExtra[] = grupos.map((grupo) => ({
    clave: `grupo-${grupo.id}`,
    etiqueta: grupo.pestana,
    campos: ['datos'],
    contenido: ({ coleccion, id: idDoc }) => <PestanaDeGrupo coleccion={coleccion} grupo={grupo} id={idDoc} />,
  }))

  return (
    <Editor
      base={basesDe(espacio, sitio.id, taxonomia.id)}
      coleccion="terminos"
      espacio={espacio}
      etiquetas={{ singular: taxonomia.nombre, plural: taxonomia.nombrePlural }}
      id={idNumerico}
      inicialExtra={{ taxonomia: taxonomia.id }}
      // La taxonomía la pone la dirección y no cambia; «Cuelga de», solo en una jerárquica.
      ocultarCampos={['taxonomia', 'datos', ...(taxonomia.jerarquica ? [] : ['padre'])]}
      pestanasExtra={pestanas}
      sitio={sitio.id}
      soloLectura={!puedeEditar}
    />
  )
}
